import { Link, useNavigate } from "react-router-dom";
import { ScanLine, LogOut, LayoutDashboard, QrCode, Sun, Moon, BookOpen, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";

export const Shell = ({ children, wide = false, bare = false }) => {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen grain">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#090b0e]/85 border-b border-[#252f3e]/70">
        <div className="mx-auto max-w-[1400px] px-4 lg:px-8 py-3.5 flex items-center justify-between">
          <Link to="/" data-testid="brand-home-link" className="flex items-center gap-2.5">
            <span className="grid place-items-center w-8 h-8 rounded-md bg-[#00e599] text-[#090b0e]">
              <QrCode size={18} strokeWidth={2.6} />
            </span>
            <span className="font-display text-xl font-black uppercase tracking-wide">
              Ark<span className="text-[#00e599]">Pass</span>
            </span>
          </Link>
          <nav className="flex items-center gap-4 sm:gap-6">
            <button
              onClick={toggle}
              data-testid="theme-toggle-btn"
              title={theme === "light" ? "Switch to dark mode" : "Switch to light mode"}
              className="btn-ghost"
            >
              {theme === "light" ? <Moon size={16} /> : <Sun size={16} />}
              <span className="hidden lg:inline">{theme === "light" ? "Dark" : "Light"}</span>
            </button>
            {/* Guest-facing reply pages hide every navigation link so nobody wanders off. */}
            {!bare && (
              <Link to="/retrieve-qr" data-testid="nav-retrieve-link" className="btn-ghost hidden sm:flex">
                <ScanLine size={15} /> Find my pass
              </Link>
            )}
            {!bare && user && user.role !== "staff" && (
              <Link to="/moderator/team" data-testid="nav-team-link" className="btn-ghost hidden sm:flex">
                <Users size={15} /> Team
              </Link>
            )}
            {!bare && (
              <Link to="/help" data-testid="nav-help-link" className="btn-ghost hidden sm:flex">
                <BookOpen size={15} /> Manual
              </Link>
            )}
            {!bare && (user ? (
              <>
                <Link to="/moderator/dashboard" data-testid="nav-dashboard-link" className="btn-ghost">
                  <LayoutDashboard size={15} /> Dashboard
                </Link>
                <button
                  data-testid="nav-logout-btn"
                  onClick={async () => {
                    await logout();
                    navigate("/");
                  }}
                  className="btn-ghost"
                >
                  <LogOut size={15} /> <span className="hidden sm:inline">Sign out</span>
                </button>
              </>
            ) : (
              <Link to="/moderator/login" data-testid="nav-login-link" className="btn-primary py-2 px-4">
                Organiser login
              </Link>
            ))}
          </nav>
        </div>
      </header>
      <main className={`relative z-10 mx-auto px-4 lg:px-8 py-10 ${wide ? "max-w-[1400px]" : "max-w-5xl"}`}>
        {children}
      </main>
      <footer className="border-t border-[#252f3e]/60 mt-16">
        <div className="mx-auto max-w-[1400px] px-4 lg:px-8 py-8 flex flex-col sm:flex-row gap-3 justify-between text-xs text-slate-500">
          <span>Ark Media — QR registration &amp; check-in</span>
          {!bare && (
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-slate-300">
              Privacy &amp; data retention (PDPA)
            </Link>
          )}
        </div>
      </footer>
    </div>
  );
};
